import React, { useRef, useState } from 'react';

import {
  arrow,
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';

const ARROW_HEIGHT = 7;
const GAP = 3;

type UseHoverTooltipOptions = {
  enabled?: boolean;
  placement?: Placement;
  showArrow?: boolean;
};

type UseFloatingReturn = ReturnType<typeof useFloating>;
type UseInteractionsReturn = ReturnType<typeof useInteractions>;

type UseHoverTooltipReturn = {
  isOpen: boolean;
  refs: UseFloatingReturn['refs'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
  getReferenceProps: UseInteractionsReturn['getReferenceProps'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
  context: UseFloatingReturn['context'];
  arrowRef: React.MutableRefObject<null>;
};

export const useFloatingTooltip = (
  options: UseHoverTooltipOptions = {}
): UseHoverTooltipReturn => {
  const { enabled = true, placement = 'top', showArrow } = options;

  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const middleware = [
    offset(showArrow ? ARROW_HEIGHT + GAP : GAP),
    flip({ fallbackAxisSideDirection: 'start' }),
    shift(),
    ...(showArrow ? [arrow({ element: arrowRef })] : []),
  ];

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && enabled,
    onOpenChange: setIsOpen,
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  return {
    isOpen: isOpen && enabled,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    context,
    arrowRef,
  };
};
